import React from 'react';
import styled from 'styled-components';
import PropTypes from 'prop-types';
import queryString from 'query-string';
import getLogger from '../../../utils/logger';
import { IntegrationMode } from '../../../utils/IntegrationConstants';
import useIntegrationContext from '../../hooks/useIntegrationContext';
import CheckIntegrationSetting from '../../../utils/integrationHelper';

const log = getLogger();

const LayoutGrid = styled.div`
  --header-height: 3.25rem;
  display: -ms-grid;
  display: grid;
  @supports (-webkit-touch-callout: none) {
    position: fixed;
  }
  -ms-grid-rows: ${(props) =>
    props.showStickyHeader
      ? '3.25rem 2.4375rem minmax(10rem, 100%)'
      : '3.25rem minmax(10rem, 100%)'};
  grid-template-rows: ${(props) =>
    props.showStickyHeader
      ? '3.25rem 2.4375rem minmax(10rem, auto)'
      : '3.25rem minmax(10rem, auto)'};
  -ms-grid-columns: 5.625rem 1fr;
  grid-template-columns: 5.625rem 1fr;
  width: 100%;
  height: 100vh;
  max-height: -webkit-fill-available;

  .header {
    -ms-grid-row: 1;
    grid-row-start: 1;
    -ms-grid-column: span 2;
    grid-column-start: 1;
    -ms-grid-column-span: 2;
    grid-column-end: span 2;
  }

  .side-bar {
    -ms-grid-column: 1;
    grid-column-start: 1;
    -ms-grid-row: 2;
    grid-row-start: 2;
    -ms-grid-row-span: ${(props) => (props.showStickyHeader ? '2' : '1')};
    grid-row-end: ${(props) => (props.showStickyHeader ? 'span 2' : 'span 1')};
  }
  .content {
    -ms-grid-column: 2;
    grid-column-start: 2;
    -ms-grid-row: ${(props) => (props.showStickyHeader ? '3' : '2')};
    grid-row-start: ${(props) => (props.showStickyHeader ? '3' : '2')};
    position: relative;
    overflow: auto;
  }
  .sticky-header {
    -ms-grid-column: 2;
    grid-column-start: 2;
    -ms-grid-row: 2;
    grid-row-start: 2;
    position: relative;
    overflow: auto;
  }
  .modal-backdrop {
    visibility: hidden;
    position: fixed;
    inset: 0;
    z-index: 0;
  }
  .header-backdrop {
    height: 3.25rem;
  }
  .sidebar-backdrop {
    width: 5.625rem;
    top: 3.25rem;
  }
  .modal-add-backdrop {
    visibility: visible;
    // This z-index should be more than the z-index value of header component(1100).
    z-index: 1200;
    background-color: #000;
    opacity: 0.5;
    transition: opacity 0.15s linear;
  }
`;

const IntegrationLayoutGrid = styled(LayoutGrid)`
  -ms-grid-rows: ${(props) =>
    props.showStickyHeader ? '2rem 2.4375rem minmax(10rem, 100%)' : '2rem minmax(10rem, 100%)'};
  grid-template-rows: ${(props) =>
    props.showStickyHeader ? '2rem 2.4375rem minmax(10rem, auto)' : '2rem minmax(10rem, auto)'};
  -ms-grid-columns: 1fr;
  grid-template-columns: 1fr;

  .content {
    -ms-grid-column: 1;
    grid-column-start: 1;
    -ms-grid-row: ${(props) => (props.showStickyHeader ? '3' : '2')};
    grid-row-start: ${(props) => (props.showStickyHeader ? '3' : '2')};
  }
`;

/**
  AppLayout
* */
function AppLayout({ showStickyHeader, children }) {
  const { integrationContext } = useIntegrationContext();

  // only show layout if rcore is top
  let showLayout = true;
  // this checks if we are in our own domain and also if we are top
  if (window.top !== window.self) {
    try {
      // If our parent contains rcore, then we are in an rcore iFrame
      // else we are in an external iFrame and the current window is the top frame
      if (window.parent.location.href.toLowerCase().includes('rcore')) {
        showLayout = false;
      }
    } catch {
      log.info('Parent location is external');
    }
  }

  const Grid = CheckIntegrationSetting(IntegrationMode.Teams, integrationContext.mode)
    ? LayoutGrid
    : IntegrationLayoutGrid;

  if (showLayout) {
    return (
      <>
        <Grid id="rcoreLayout" showStickyHeader={showStickyHeader}>
          {children}
        </Grid>
      </>
    );
  }

  const containerExists = window.self.document.getElementById('CoreIFrame') == null ? 0 : 1;
  const container =
    containerExists === 1
      ? window.self.document.getElementById('CoreIFrame')
      : window.top.document.getElementById('CoreIFrame');

  if (container != null) {
    if (window.location.href.toLowerCase().indexOf('/rcore/c/') > -1) {
      const url = window.location.href.replace(/\/rcore\/c\//i, '/core/');
      const parsedUrl = queryString.parseUrl(url);
      if (!parsedUrl.query.rc) {
        parsedUrl.query.rc = 1;
      }
      window.location.href = queryString.stringifyUrl(parsedUrl);
    } else if (window.location.href.toLowerCase().indexOf('/rcore') > -1) {
      const parsedUrl = queryString.parseUrl(window.location.href);
      if (parsedUrl.query.rc) {
        delete parsedUrl.query.rc;
      }
      if (containerExists === 1) window.parent.location.href = queryString.stringifyUrl(parsedUrl);
      else window.top.location.href = queryString.stringifyUrl(parsedUrl);
    }
  }
  return <div />;
}

AppLayout.propTypes = {
  /**
   Flag to show static emulation strip above the page content.
  * */
  showStickyHeader: PropTypes.bool,
  /**
   Children elements
  * */
  children: PropTypes.node
};

AppLayout.defaultProps = {
  showStickyHeader: false,
  children: null
};

export default AppLayout;
